import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { ArrowLeft, Upload, Trash2, CheckCircle, AlertTriangle, Pencil, Save } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  withCredentials: true
});

// Helper to build correct GIF URL (handles both Cloudinary and local)
const buildGifUrl = (gifUrl) => {
  if (!gifUrl) return '';
  // If it's already an absolute URL (Cloudinary), return as-is
  if (gifUrl.startsWith('http://') || gifUrl.startsWith('https://')) {
    return gifUrl;
  }
  // Otherwise, it's a local path, prepend backend URL
  return `${process.env.REACT_APP_BACKEND_URL}${gifUrl}`;
};

const AdminGifs = () => {
  const navigate = useNavigate();
  const [gifs, setGifs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedClass, setSelectedClass] = useState('tank');
  
  // Species names state
  const [speciesNames, setSpeciesNames] = useState({});
  const [editingPosition, setEditingPosition] = useState(null);
  const [tempNames, setTempNames] = useState({ name_stage_1: '', name_stage_2: '', name_stage_3: '' });
  const [savingNames, setSavingNames] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [gifToDelete, setGifToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadGifs();
  }, []);

  // Load species names when class changes
  useEffect(() => {
    if (selectedClass !== 'egg') {
      loadSpeciesNamesForClass();
    }
  }, [selectedClass, gifs]);

  const loadGifs = async () => {
    try {
      const response = await API.get('/admin/gifs');
      setGifs(response.data);
    } catch (error) {
      console.error('Failed to load GIFs:', error);
      toast.error('Erro ao carregar GIFs');
    } finally {
      setLoading(false);
    }
  };

  const loadSpeciesNamesForClass = async () => {
    // Extract species names from GIF data for current class
    const classGifs = gifs.filter(g => g.class_type === selectedClass);
    const namesMap = {};
    
    classGifs.forEach(gif => {
      if (gif.position >= 0 && !namesMap[gif.position]) {
        namesMap[gif.position] = {
          name_stage_1: gif.name_stage_1 || '',
          name_stage_2: gif.name_stage_2 || '',
          name_stage_3: gif.name_stage_3 || ''
        };
      }
    });
    
    setSpeciesNames(namesMap);
  };

  const handleEditSpeciesNames = (position) => {
    const currentNames = speciesNames[position] || { name_stage_1: '', name_stage_2: '', name_stage_3: '' };
    setTempNames({ ...currentNames });
    setEditingPosition(position);
  };

  const handleSaveSpeciesNames = async () => {
    if (editingPosition === null) return;
    
    setSavingNames(true);
    try {
      await API.post('/admin/gifs/species-names', {
        class_type: selectedClass,
        position: editingPosition,
        ...tempNames
      });
      
      // Update local state
      setSpeciesNames(prev => ({
        ...prev,
        [editingPosition]: { ...tempNames }
      }));
      
      toast.success('Nomes das espécies salvos com sucesso!');
      setEditingPosition(null);
      loadGifs(); // Reload to get updated data
    } catch (error) {
      console.error('Failed to save species names:', error);
      toast.error('Erro ao salvar nomes das espécies');
    } finally {
      setSavingNames(false);
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);

    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);

      try {
        await API.post('/admin/gifs/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        toast.success(`${file.name} enviado com sucesso!`);
      } catch (error) {
        console.error('Upload failed:', error);
        toast.error(`Erro ao enviar ${file.name}`);
      }
    }

    setUploading(false);
    loadGifs();
    e.target.value = '';
  };

  const handleAssignGif = async (gifId, classType, position, stage = 1) => {
    console.log('Assigning GIF:', { gifId, classType, position, stage });
    try {
      const response = await API.post(`/admin/gifs/${gifId}/assign`, {
        class_type: classType,
        position: position,
        stage: stage
      });
      console.log('Assignment response:', response.data);
      toast.success('GIF atribuído com sucesso!');
      loadGifs();
    } catch (error) {
      console.error('Assignment failed:', error);
      console.error('Error details:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Erro ao atribuir GIF');
    }
  };

  const openDeleteDialog = (gifId) => {
    console.log('Opening delete dialog for:', gifId);
    setGifToDelete(gifId);
    setDeleteDialogOpen(true);
  };

  const handleDeleteGif = async () => {
    if (!gifToDelete) return;
    
    console.log('Deleting GIF:', gifToDelete);
    setDeleting(true);
    
    try {
      const response = await API.delete(`/admin/gifs/${gifToDelete}`);
      console.log('Delete response:', response.data);
      toast.success('GIF deletado com sucesso!');
      setDeleteDialogOpen(false);
      setGifToDelete(null);
      await loadGifs();
    } catch (error) {
      console.error('Delete failed:', error);
      console.error('Error response:', error.response);
      console.error('Error details:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Erro ao deletar GIF');
    } finally {
      setDeleting(false);
    }
  };

  const getGifsByClass = (classType) => {
    return gifs.filter(g => g.class_type === classType).sort((a, b) => a.position - b.position);
  };

  const getUnassignedGifs = () => {
    return gifs.filter(g => g.class_type === 'unassigned');
  };

  const getPositionName = (position) => {
    return position === -1 ? 'Ovo' : `Posição ${position + 1}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <nav className="bg-white border-b border-slate-200">
        <div className="container mx-auto px-4 py-4">
          <Button
            data-testid="back-btn"
            onClick={() => navigate('/teacher/dashboard')}
            variant="ghost"
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar
          </Button>
        </div>
      </nav>

      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-outfit font-bold text-slate-900 mb-2" data-testid="admin-title">
            Gerenciar GIFs dos Monstros
          </h1>
          <p className="text-slate-600 font-nunito">
            Faça upload e organize os GIFs que serão usados para cada classe de monstro
          </p>
        </div>

        {/* Upload Section */}
        <Card className="p-6 mb-8">
          <h2 className="text-xl font-outfit font-bold text-slate-900 mb-4">Upload de GIFs</h2>
          <div className="flex items-center gap-4">
            <label
              htmlFor="gif-upload"
              className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-white h-11 px-6 rounded-xl font-bold cursor-pointer transition-all"
            >
              <Upload className="w-5 h-5" />
              {uploading ? 'Enviando...' : 'Selecionar GIFs'}
            </label>
            <input
              id="gif-upload"
              type="file"
              accept="image/gif,image/png,image/jpeg"
              multiple
              onChange={handleFileUpload}
              disabled={uploading}
              className="hidden"
              data-testid="gif-upload-input"
            />
            <p className="text-sm text-slate-500">
              Aceita GIF, PNG, JPG. Você pode selecionar múltiplos arquivos.
            </p>
          </div>
        </Card>

        {/* Unassigned GIFs */}
        {getUnassignedGifs().length > 0 && (
          <Card className="p-6 mb-8">
            <h2 className="text-xl font-outfit font-bold text-slate-900 mb-4">
              GIFs Não Atribuídos ({getUnassignedGifs().length})
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {getUnassignedGifs().map((gif) => (
                <GifCard
                  key={gif.gif_id}
                  gif={gif}
                  onAssign={handleAssignGif}
                  onDelete={openDeleteDialog}
                  showAssignment={true}
                />
              ))}
            </div>
          </Card>
        )}

        {/* Class Tabs */}
        <div className="mb-4">
          <div className="flex gap-2">
            {['tank', 'warrior', 'support', 'egg'].map((type) => (
              <Button
                key={type}
                data-testid={`${type}-tab`}
                onClick={() => setSelectedClass(type)}
                variant={selectedClass === type ? 'default' : 'outline'}
                className="capitalize"
              >
                {type === 'tank' ? '🛡️ Tank' : type === 'warrior' ? '⚔️ Atacante' : type === 'support' ? '💚 Suporte' : '🥚 Ovo'}
              </Button>
            ))}
          </div>
        </div>

        {/* Assigned GIFs Grid */}
        <Card className="p-6">
          <h2 className="text-xl font-outfit font-bold text-slate-900 mb-4 capitalize">
            {selectedClass === 'tank' ? '🛡️ Tank' : selectedClass === 'warrior' ? '⚔️ Guerreiro' : selectedClass === 'support' ? '💚 Suporte' : '🥚 Ovo'}
          </h2>
          {selectedClass === 'egg' ? (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              <EggSlot
                gif={gifs.find(g => g.class_type === 'egg' && g.position === -1)}
                onAssign={handleAssignGif}
                onDelete={openDeleteDialog}
              />
            </div>
          ) : (
            <div className="space-y-8">
              {Array.from({ length: 15 }, (_, i) => {
                const posNames = speciesNames[i] || {};
                return (
                <div key={i} className="border-t border-slate-200 pt-6 first:border-t-0 first:pt-0">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-outfit font-bold text-slate-700">
                      Posição {i + 1} - 3 Estágios de Evolução
                    </h3>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEditSpeciesNames(i)}
                      className="text-blue-600 hover:text-blue-700"
                      data-testid={`edit-species-names-${i}`}
                    >
                      <Pencil className="w-4 h-4 mr-1" />
                      Editar Nomes
                    </Button>
                  </div>
                  
                  {/* Species Names Display */}
                  {(posNames.name_stage_1 || posNames.name_stage_2 || posNames.name_stage_3) && (
                    <div className="bg-blue-50 rounded-lg p-3 mb-3 flex gap-4 text-sm">
                      <span className="text-slate-600">
                        <strong>Est.1:</strong> {posNames.name_stage_1 || '-'}
                      </span>
                      <span className="text-slate-600">
                        <strong>Est.2:</strong> {posNames.name_stage_2 || '-'}
                      </span>
                      <span className="text-slate-600">
                        <strong>Est.3:</strong> {posNames.name_stage_3 || '-'}
                      </span>
                    </div>
                  )}
                  
                  <div className="grid grid-cols-3 gap-4">
                    {[1, 2, 3].map((stage) => {
                      const gif = gifs.find(g => 
                        g.class_type === selectedClass && 
                        g.position === i && 
                        g.stage === stage
                      );
                      return (
                        <GifSlot
                          key={`${i}-${stage}`}
                          position={i}
                          stage={stage}
                          classType={selectedClass}
                          gif={gif}
                          onAssign={handleAssignGif}
                          onDelete={openDeleteDialog}
                          stageName={stage === 1 ? posNames.name_stage_1 : stage === 2 ? posNames.name_stage_2 : posNames.name_stage_3}
                        />
                      );
                    })}
                  </div>
                </div>
              )})}
            </div>
          )}
        </Card>

        {/* Edit Species Names Dialog */}
        <Dialog open={editingPosition !== null} onOpenChange={(open) => !open && setEditingPosition(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-blue-600">
                <Pencil className="w-5 h-5" />
                Editar Nomes da Espécie - Posição {editingPosition !== null ? editingPosition + 1 : ''}
              </DialogTitle>
              <DialogDescription>
                Defina os nomes para cada estágio de evolução. Esses nomes aparecerão ao chocar o ovo e evoluir.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm font-bold text-slate-700 block mb-1">
                  🥚 Nome Estágio 1 (Ex: Bulbassauro)
                </label>
                <Input
                  value={tempNames.name_stage_1}
                  onChange={(e) => setTempNames(prev => ({ ...prev, name_stage_1: e.target.value }))}
                  placeholder="Nome do Pokémon no estágio 1"
                  data-testid="species-name-stage-1"
                />
              </div>
              <div>
                <label className="text-sm font-bold text-slate-700 block mb-1">
                  🐣 Nome Estágio 2 (Ex: Ivyssauro)
                </label>
                <Input
                  value={tempNames.name_stage_2}
                  onChange={(e) => setTempNames(prev => ({ ...prev, name_stage_2: e.target.value }))}
                  placeholder="Nome do Pokémon no estágio 2"
                  data-testid="species-name-stage-2"
                />
              </div>
              <div>
                <label className="text-sm font-bold text-slate-700 block mb-1">
                  🦎 Nome Estágio 3 (Ex: Venussauro)
                </label>
                <Input
                  value={tempNames.name_stage_3}
                  onChange={(e) => setTempNames(prev => ({ ...prev, name_stage_3: e.target.value }))}
                  placeholder="Nome do Pokémon no estágio 3"
                  data-testid="species-name-stage-3"
                />
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={() => setEditingPosition(null)}
                disabled={savingNames}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSaveSpeciesNames}
                disabled={savingNames}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="save-species-names-btn"
              >
                {savingNames ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                    Salvando...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-1" />
                    Salvar Nomes
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="w-5 h-5" />
                Confirmar Exclusão
              </DialogTitle>
              <DialogDescription>
                Tem certeza que deseja deletar este GIF? Esta ação não pode ser desfeita.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setDeleteDialogOpen(false);
                  setGifToDelete(null);
                }}
                disabled={deleting}
              >
                Cancelar
              </Button>
              <Button
                variant="destructive"
                onClick={handleDeleteGif}
                disabled={deleting}
              >
                {deleting ? 'Deletando...' : 'Deletar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

const GifCard = ({ gif, onAssign, onDelete, showAssignment }) => {
  const [assigning, setAssigning] = useState(false);
  const [classType, setClassType] = useState('tank');
  const [position, setPosition] = useState(0);
  const [stage, setStage] = useState(1);

  const handleAssign = async () => {
    setAssigning(true);
    await onAssign(gif.gif_id, classType, classType === 'egg' ? -1 : position, classType === 'egg' ? -1 : stage);
    setAssigning(false);
  };

  const isEgg = classType === 'egg';

  return (
    <div className="bg-white rounded-xl border-2 border-slate-100 p-3 hover:border-blue-200 transition-all">
      <div className="aspect-square bg-slate-50 rounded-lg mb-2 flex items-center justify-center overflow-hidden">
        <img
          src={buildGifUrl(gif.url)}
          alt="GIF"
          className="w-full h-full object-contain"
        />
      </div>
      {gif.class_type !== 'unassigned' && (
        <div className="text-xs text-green-600 font-bold mb-2 flex items-center gap-1">
          <CheckCircle className="w-3 h-3" />
          Pos {gif.position + 1} - Est {gif.stage}
        </div>
      )}
      {showAssignment && (
        <div className="space-y-2">
          <select
            value={classType}
            onChange={(e) => setClassType(e.target.value)}
            className="w-full h-8 text-xs border border-slate-200 rounded-md px-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="tank">Tank</option>
            <option value="warrior">Atacante</option>
            <option value="support">Suporte</option>
            <option value="egg">Ovo</option>
          </select>
          <select
            value={position.toString()}
            onChange={(e) => setPosition(parseInt(e.target.value))}
            disabled={isEgg}
            className={`w-full h-8 text-xs border border-slate-200 rounded-md px-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary ${isEgg ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {Array.from({ length: 15 }, (_, i) => (
              <option key={i} value={i.toString()}>Posição {i + 1}</option>
            ))}
          </select>
          <select
            value={stage.toString()}
            onChange={(e) => setStage(parseInt(e.target.value))}
            disabled={isEgg}
            className={`w-full h-8 text-xs border border-slate-200 rounded-md px-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary ${isEgg ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <option value="1">Estágio 1</option>
            <option value="2">Estágio 2</option>
            <option value="3">Estágio 3</option>
          </select>
          <div className="flex gap-1">
            <Button
              onClick={handleAssign}
              disabled={assigning}
              size="sm"
              className="flex-1 h-8 text-xs"
            >
              Atribuir
            </Button>
          </div>
        </div>
      )}
      <Button
        onClick={() => onDelete(gif.gif_id)}
        variant="outline"
        size="sm"
        className="w-full h-8 text-xs text-red-600 hover:text-red-700 mt-2"
      >
        <Trash2 className="w-3 h-3 mr-1" />
        Deletar
      </Button>
    </div>
  );
};

const GifSlot = ({ position, stage, classType, gif, onAssign, onDelete, stageName }) => {
  return (
    <div className="bg-slate-50 rounded-xl border-2 border-dashed border-slate-200 p-2 flex flex-col items-center">
      {gif ? (
        <>
          <div className="w-20 h-20 flex items-center justify-center mb-2">
            <img
              src={buildGifUrl(gif.url)}
              alt={`Position ${position} Stage ${stage}`}
              className="max-w-full max-h-full object-contain"
            />
          </div>
          <p className="text-xs font-bold text-slate-700">Est {stage}</p>
          {stageName && (
            <p className="text-xs text-blue-600 font-semibold truncate max-w-full px-1">{stageName}</p>
          )}
          <Button
            onClick={() => onDelete(gif.gif_id)}
            variant="ghost"
            size="sm"
            className="h-6 text-xs text-red-600 mt-1 hover:bg-red-50"
          >
            <Trash2 className="w-3 h-3 mr-1" />
            Deletar
          </Button>
        </>
      ) : (
        <div className="text-center py-4">
          <div className="text-2xl mb-2">
            {stage === 1 ? '🥚' : stage === 2 ? '🐣' : '🦎'}
          </div>
          <p className="text-xs text-slate-500">Estágio {stage}</p>
          {stageName ? (
            <p className="text-xs text-blue-500 font-semibold">{stageName}</p>
          ) : (
            <p className="text-xs text-slate-400">Vazio</p>
          )}
        </div>
      )}
    </div>
  );
};

const EggSlot = ({ gif, onAssign, onDelete }) => {
  return (
    <div className="bg-slate-50 rounded-xl border-2 border-dashed border-slate-200 p-2 flex flex-col items-center">
      {gif ? (
        <>
          <div className="w-20 h-20 flex items-center justify-center mb-2">
            <img
              src={buildGifUrl(gif.url)}
              alt="Egg GIF"
              className="max-w-full max-h-full object-contain"
            />
          </div>
          <p className="text-xs font-bold text-slate-700">GIF do Ovo</p>
          <Button
            onClick={() => onDelete(gif.gif_id)}
            variant="ghost"
            size="sm"
            className="h-6 text-xs text-red-600 mt-1 hover:bg-red-50"
          >
            <Trash2 className="w-3 h-3 mr-1" />
            Deletar
          </Button>
        </>
      ) : (
        <div className="text-center py-4">
          <div className="text-4xl mb-2">🥚</div>
          <p className="text-xs text-slate-500">GIF do Ovo</p>
          <p className="text-xs text-slate-400">Vazio</p>
        </div>
      )}
    </div>
  );
};

const getPositionName = (position) => {
  return position === -1 ? 'Ovo' : `Pos ${position + 1}`;
};

export default AdminGifs;
